/**
 * Analítica del tema AMR.
 *
 * - PostHog solo se carga en el dominio principal de producción.
 * - Respeta analyticsProcessingAllowed() de Shopify Customer Privacy.
 * - Autocapture permanece apagado; Session Replay y heatmaps respetan el
 *   consentimiento y usan controles de privacidad explícitos.
 * - Los píxeles de Google, Meta, TikTok y demás apps siguen bajo Shopify.
 */
(function() {
  'use strict';

  var config = window.TMSAnalyticsConfig;
  var analytics = window.TMSAnalytics || { queue: [] };
  window.TMSAnalytics = analytics;

  var hostname = window.location.hostname.toLowerCase();
  var primaryHost = config ? String(config.productionHost || '').toLowerCase() : '';
  var rootHost = primaryHost.replace(/^www\./, '');
  var productionHosts = [primaryHost, rootHost, rootHost ? 'www.' + rootHost : ''];
  var isProduction = Boolean(config && config.projectKey && productionHosts.indexOf(hostname) !== -1);
  var debugParams = new URLSearchParams(window.location.search);
  var debugRequested = debugParams.get('tms_posthog_debug') === '1';
  var debugDelayRequested = debugParams.get('tms_posthog_delay') === '1';
  try {
    if (debugRequested) sessionStorage.setItem('tms:posthog_debug', '1');
    debugRequested = debugRequested || sessionStorage.getItem('tms:posthog_debug') === '1';
  } catch (error) { /* sessionStorage bloqueado: el parámetro sigue funcionando en esta página */ }
  var isEnabled = Boolean(config && config.projectKey && (isProduction || debugRequested));
  var pending = Array.isArray(analytics.queue) ? analytics.queue.slice() : [];
  var analyticsAllowed = false;
  var consentResolved = false;
  var sdkReady = false;
  var sdkStarted = false;
  var sdkStartTimer = null;
  var sdkIdleHandle = null;
  var replayStartTimer = null;
  var replayIdleHandle = null;
  var replayStarted = false;
  var replayForced = false;
  var shopifyClientIdentityApplied = false;
  var shopifyClientIdentityAttempts = 0;
  var SHOPIFY_CLIENT_COOKIE = 'tms_shopify_client_id';
  var MARKETING_ATTRIBUTION_COOKIE = 'tms_marketing_attribution_v1';
  var MARKETING_ATTRIBUTION_MAX_AGE = 60 * 60 * 24 * 90;
  var MARKETING_QUERY_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_id',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'fbclid',
    'gclid',
    'gbraid',
    'wbraid',
    'ttclid',
    'msclkid'
  ];
  var marketingAttribution = {};
  var PERFORMANCE_SAMPLE_KEY = 'tms:posthog_web_vitals_sample';
  var REPLAY_SAMPLE_KEY = 'tms:posthog_replay_sample';
  var REPLAY_FORCE_KEY = 'tms:posthog_replay_force';

  analytics.queue = [];
  analytics.enabled = isEnabled;

  if (!isEnabled) {
    analytics.capture = function() {};
    return;
  }

  function compact(properties) {
    var result = {};
    Object.keys(properties || {}).forEach(function(key) {
      var value = properties[key];
      if (value !== undefined && value !== null && value !== '') result[key] = value;
    });
    return result;
  }

  function pageContext() {
    return compact({
      tms_page_type: config.pageType,
      tms_template: config.template,
      tms_currency: config.currency,
      tms_shop_domain: config.shopDomain,
      tms_environment: isProduction ? 'production' : 'test',
      tms_collection_handle: config.collectionHandle
    });
  }

  function isSensitiveReplayPage() {
    var pageType = String(config.pageType || '').toLowerCase();
    var templateName = String(config.template || '').toLowerCase();
    return pageType.indexOf('customers/') === 0 ||
      templateName.indexOf('customers/') === 0 ||
      pageType === 'account' ||
      templateName === 'account';
  }

  function isHighIntentReplayPage() {
    var pageType = String(config.pageType || '').toLowerCase();
    var templateName = String(config.template || '').toLowerCase();
    return pageType === 'cart' || templateName === 'cart';
  }

  function isHomepageMobileReplayDiagnosticActive() {
    var pageType = String(config.pageType || '').toLowerCase();
    var templateName = String(config.template || '').toLowerCase();
    if (pageType !== 'index' && templateName !== 'index') return false;

    var diagnosticUntil = Date.parse(config.homepageMobileReplayUntil || '');
    if (!isFinite(diagnosticUntil) || Date.now() > diagnosticUntil) return false;

    if (window.matchMedia) return window.matchMedia('(max-width: 767px)').matches;
    return window.innerWidth < 768;
  }

  function isReplayForcedForSession() {
    if (replayForced) return true;
    try {
      replayForced = sessionStorage.getItem(REPLAY_FORCE_KEY) === '1';
    } catch (error) { /* La bandera en memoria sigue funcionando. */ }
    return replayForced;
  }

  function forceReplayForSession() {
    replayForced = true;
    try {
      sessionStorage.setItem(REPLAY_FORCE_KEY, '1');
    } catch (error) { /* La bandera en memoria sigue funcionando. */ }
  }

  function shouldSampleReplay() {
    if (debugRequested && !isProduction) return true;
    if (isReplayForcedForSession()) return true;
    if (isHomepageMobileReplayDiagnosticActive()) return true;

    var sampleRate = Number(config.replaySampleRate);
    if (!isFinite(sampleRate)) sampleRate = 0.20;
    sampleRate = Math.max(0, Math.min(1, sampleRate));
    if (sampleRate === 0) return false;
    if (sampleRate === 1) return true;

    /* La selección se conserva durante toda la pestaña para que una misma
       sesión no entre y salga del muestreo al navegar entre páginas. */
    try {
      var storedDecision = sessionStorage.getItem(REPLAY_SAMPLE_KEY);
      if (storedDecision === '1' || storedDecision === '0') return storedDecision === '1';
      var sampled = Math.random() < sampleRate;
      sessionStorage.setItem(REPLAY_SAMPLE_KEY, sampled ? '1' : '0');
      return sampled;
    } catch (error) {
      return Math.random() < sampleRate;
    }
  }

  function shouldCaptureWebVitals() {
    /* En desarrollo, el modo debug siempre mide para facilitar la validación. */
    if (debugRequested && !isProduction) return true;

    var sampleRate = Number(config.performanceSampleRate);
    if (!isFinite(sampleRate)) sampleRate = 0.10;
    sampleRate = Math.max(0, Math.min(1, sampleRate));
    if (sampleRate === 0) return false;
    if (sampleRate === 1) return true;

    /* La decisión queda fija durante la sesión de la pestaña para evitar
       sesgos entre páginas rápidas y lentas del mismo recorrido. */
    try {
      var storedDecision = sessionStorage.getItem(PERFORMANCE_SAMPLE_KEY);
      if (storedDecision === '1' || storedDecision === '0') return storedDecision === '1';
      var sampled = Math.random() < sampleRate;
      sessionStorage.setItem(PERFORMANCE_SAMPLE_KEY, sampled ? '1' : '0');
      return sampled;
    } catch (error) {
      return Math.random() < sampleRate;
    }
  }

  function readCookie(name) {
    var prefix = name + '=';
    var cookies = document.cookie ? document.cookie.split(';') : [];
    for (var index = 0; index < cookies.length; index += 1) {
      var cookie = cookies[index].trim();
      if (cookie.indexOf(prefix) === 0) {
        try {
          return decodeURIComponent(cookie.slice(prefix.length));
        } catch (error) {
          return cookie.slice(prefix.length);
        }
      }
    }
    return null;
  }

  function boundedString(value, maxLength) {
    if (value === undefined || value === null) return undefined;
    return String(value).trim().slice(0, maxLength || 500) || undefined;
  }

  function writeCookie(name, value, maxAge) {
    var secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = name + '=' + encodeURIComponent(value) +
      '; path=/; max-age=' + maxAge + '; SameSite=Lax' + secure;
  }

  function deleteCookie(name) {
    writeCookie(name, '', 0);
  }

  function parseMarketingAttribution(value) {
    if (!value) return {};
    try {
      var parsed = JSON.parse(value);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      var result = {};
      MARKETING_QUERY_KEYS.forEach(function(key) {
        var property = boundedString(parsed[key]);
        if (property) result[key] = property;
      });
      ['tms_meta_campaign_id', 'tms_meta_adset_id', 'tms_meta_ad_id'].forEach(function(key) {
        var property = boundedString(parsed[key], 100);
        if (property) result[key] = property;
      });
      var quality = boundedString(parsed.tms_attribution_quality, 50);
      var capturedAt = boundedString(parsed.tms_attribution_captured_at, 50);
      var landingPath = boundedString(parsed.tms_attribution_landing_path, 500);
      if (quality) result.tms_attribution_quality = quality;
      if (capturedAt) result.tms_attribution_captured_at = capturedAt;
      if (landingPath) result.tms_attribution_landing_path = landingPath;
      return result;
    } catch (error) {
      return {};
    }
  }

  function isMetaAttribution(attribution) {
    var source = String(attribution.utm_source || '').toLowerCase();
    return Boolean(
      attribution.fbclid ||
      source === 'facebook' ||
      source === 'instagram' ||
      source === 'meta'
    );
  }

  function addMetaIds(attribution) {
    if (!isMetaAttribution(attribution)) return attribution;
    /* Meta usa utm_id como ID de campaña en algunos formatos y utm_campaign
       en otros. Los normalizamos para que PostHog siempre tenga una clave de
       unión con la tabla de campañas. */
    if (!attribution.utm_campaign && attribution.utm_id) {
      attribution.utm_campaign = attribution.utm_id;
    }
    if (/^\d+$/.test(attribution.utm_campaign || '')) {
      attribution.tms_meta_campaign_id = attribution.utm_campaign;
    }
    if (/^\d+$/.test(attribution.utm_term || '')) {
      attribution.tms_meta_adset_id = attribution.utm_term;
    }
    if (/^\d+$/.test(attribution.utm_content || '')) {
      attribution.tms_meta_ad_id = attribution.utm_content;
    }
    return attribution;
  }

  function mergePartialMarketingAttribution(previous, current) {
    var hasPrevious = Object.keys(previous).length > 0;
    var hasCurrentCampaign = Boolean(current.utm_campaign || current.utm_id);

    if (!hasPrevious || hasCurrentCampaign || !isMetaAttribution(previous) || !isMetaAttribution(current)) {
      current.tms_attribution_quality = hasCurrentCampaign ? 'direct' : 'partial';
      return addMetaIds(current);
    }

    /* Un nuevo fbclid sin campaña no debe borrar una campaña conocida. Esto
       ocurre con enlaces de Meta que omiten UTMs en visitas repetidas. Los
       campos presentes en el clic nuevo ganan; el contexto faltante se hereda. */
    var merged = Object.assign({}, previous, current, {
      tms_attribution_quality: 'partial_merged'
    });
    if (current.utm_content && !/^\d+$/.test(current.utm_content)) {
      delete merged.tms_meta_ad_id;
    }
    if (current.utm_term && !/^\d+$/.test(current.utm_term)) {
      delete merged.tms_meta_adset_id;
    }
    return addMetaIds(merged);
  }

  function marketingAttributionFromUrl() {
    var attribution = {};
    MARKETING_QUERY_KEYS.forEach(function(key) {
      var value = boundedString(debugParams.get(key));
      if (value) attribution[key] = value;
    });
    if (!Object.keys(attribution).length) return {};

    /* fbclid identifica inequívocamente un clic de Meta incluso cuando una
       creatividad antigua no trae UTM completas. */
    if (attribution.fbclid && !attribution.utm_source) attribution.utm_source = 'facebook';
    if (attribution.fbclid && !attribution.utm_medium) attribution.utm_medium = 'paid_social';
    attribution.tms_attribution_captured_at = new Date().toISOString();
    attribution.tms_attribution_landing_path = boundedString(window.location.pathname, 500);
    return attribution;
  }

  function resolveMarketingAttribution() {
    var previous = parseMarketingAttribution(readCookie(MARKETING_ATTRIBUTION_COOKIE));
    var current = marketingAttributionFromUrl();
    if (Object.keys(current).length) {
      var resolved = mergePartialMarketingAttribution(previous, current);
      writeCookie(
        MARKETING_ATTRIBUTION_COOKIE,
        JSON.stringify(resolved),
        MARKETING_ATTRIBUTION_MAX_AGE
      );
      return resolved;
    }
    return previous;
  }

  function eventContext() {
    return Object.assign({}, pageContext(), marketingAttribution);
  }

  function applyShopifyClientIdentity(posthog) {
    if (shopifyClientIdentityApplied || !posthog) return shopifyClientIdentityApplied;
    var clientId = readCookie(SHOPIFY_CLIENT_COOKIE);
    if (!clientId) return false;

    var distinctId = 'shopify_client_' + clientId;
    var personProperties = config.customerId
      ? { shopify_customer_id: String(config.customerId) }
      : undefined;

    if (typeof posthog.get_distinct_id !== 'function' || posthog.get_distinct_id() !== distinctId) {
      posthog.identify(distinctId, personProperties);
    }
    shopifyClientIdentityApplied = true;
    return true;
  }

  function waitForShopifyClientIdentity(posthog) {
    if (applyShopifyClientIdentity(posthog) || shopifyClientIdentityAttempts >= 20) return;
    shopifyClientIdentityAttempts += 1;
    window.setTimeout(function() {
      waitForShopifyClientIdentity(posthog);
    }, 250);
  }

  function send(name, properties) {
    var payload = Object.assign({}, eventContext(), compact(properties));
    if (!consentResolved) {
      pending.push([name, payload]);
      return;
    }
    if (!analyticsAllowed) return;
    if (sdkReady && window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(name, payload);
      return;
    }
    pending.push([name, payload]);
  }

  analytics.capture = send;

  function flush() {
    if (!sdkReady || !analyticsAllowed || !window.posthog) return;
    var items = pending.splice(0, pending.length);
    items.forEach(function(item) {
      window.posthog.capture(item[0], Object.assign({}, eventContext(), compact(item[1])));
    });
  }

  /* Adaptación legible del snippet oficial: el SDK se descarga asíncronamente
     después del consentimiento y queda fuera del bundle crítico theme.js. */
  function installPostHogStub() {
    if (window.posthog && window.posthog.__SV) return;

    var posthog = window.posthog = window.posthog || [];
    posthog._i = [];
    posthog.__SV = 1;

    posthog.init = function(token, options, instanceName) {
      var script = document.createElement('script');
      script.type = 'text/javascript';
      script.crossOrigin = 'anonymous';
      script.async = true;
      script.src = options.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js';
      var firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode.insertBefore(script, firstScript);

      var instance = instanceName ? (posthog[instanceName] = []) : posthog;
      instance.people = instance.people || [];
      var methods = 'init capture register register_once unregister identify reset get_distinct_id get_session_id opt_in_capturing opt_out_capturing has_opted_out_capturing startSessionRecording stopSessionRecording'.split(' ');
      methods.forEach(function(method) {
        instance[method] = function() {
          instance.push([method].concat(Array.prototype.slice.call(arguments)));
        };
      });
      posthog._i.push([token, options, instanceName]);
    };
  }

  function startSdk() {
    if (sdkStarted || !analyticsAllowed) return;
    sdkStartTimer = null;
    sdkIdleHandle = null;
    sdkStarted = true;
    installPostHogStub();
    var captureWebVitals = shouldCaptureWebVitals();

    window.posthog.init(config.projectKey, {
      api_host: config.apiHost,
      defaults: '2026-05-30',
      autocapture: false,
      capture_pageview: true,
      capture_pageleave: true,
      capture_dead_clicks: false,
      capture_exceptions: false,
      capture_heatmaps: true,
      capture_performance: {
        network_timing: false,
        web_vitals: captureWebVitals,
        web_vitals_allowed_metrics: ['LCP', 'INP', 'CLS', 'FCP'],
        /* Incluye el elemento y la fase responsable de un Web Vital lento.
           Es clave para distinguir buscador, menú, carrusel o botón sin
           depender únicamente de Session Replay. */
        web_vitals_attribution: true
      },
      /* Replay se inicia manualmente más adelante. La captura de eventos,
         pageviews y Web Vitals no depende del recorder. */
      disable_session_recording: true,
      enable_recording_console_log: false,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '.ph-mask, [data-private], [data-sensitive], .customer, .account, .order',
        maskCapturedNetworkRequestFn: function(request) {
          if (request && request.name) request.name = request.name.split('?')[0];
          return request;
        }
      },
      disable_surveys: true,
      person_profiles: 'identified_only',
      respect_dnt: true,
      loaded: function(posthog) {
        sdkReady = true;
        /* El píxel personalizado de Shopify publica clientId en una cookie
           propia. Ese mismo ID enlaza la navegación con el checkout sin PII. */
        waitForShopifyClientIdentity(posthog);
        posthog.register(eventContext());
        flush();
        scheduleReplayStart(isHighIntentReplayPage() || isReplayForcedForSession());
      }
    });
  }

  function cancelScheduledReplayStart() {
    if (replayStartTimer !== null) {
      window.clearTimeout(replayStartTimer);
      replayStartTimer = null;
    }
    if (replayIdleHandle !== null && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(replayIdleHandle);
      replayIdleHandle = null;
    }
  }

  function startReplay() {
    replayStartTimer = null;
    replayIdleHandle = null;
    if (
      replayStarted ||
      !sdkReady ||
      !analyticsAllowed ||
      isSensitiveReplayPage() ||
      !window.posthog ||
      typeof window.posthog.startSessionRecording !== 'function'
    ) return;

    replayStarted = true;
    /* Sin argumentos conserva las reglas de ingestión configuradas en
       PostHog (dominios autorizados y controles del proyecto). */
    window.posthog.startSessionRecording();
  }

  function scheduleReplayStart(force) {
    if (force) forceReplayForSession();
    if (
      replayStarted ||
      !sdkReady ||
      !analyticsAllowed ||
      isSensitiveReplayPage() ||
      (!isReplayForcedForSession() && !shouldSampleReplay())
    ) return;

    /* Durante la ventana diagnóstica de la home móvil se omite solamente la
       espera adicional del recorder. El SDK y el consentimiento conservan su
       flujo habitual, y fuera de la ventana vuelve automáticamente al 20 %. */
    var startImmediately = isReplayForcedForSession() ||
      isHighIntentReplayPage() ||
      isHomepageMobileReplayDiagnosticActive();
    if (startImmediately) cancelScheduledReplayStart();
    if (replayStartTimer !== null || replayIdleHandle !== null) return;

    var configuredDelay = Number(config.replayDelayMs);
    if (!isFinite(configuredDelay)) configuredDelay = 10000;
    configuredDelay = Math.max(0, configuredDelay);
    var elapsed = window.performance && typeof window.performance.now === 'function'
      ? window.performance.now()
      : 0;
    var bypassDelay = startImmediately || (debugRequested && !debugDelayRequested);
    var remainingDelay = bypassDelay ? 0 : Math.max(0, configuredDelay - elapsed);

    replayStartTimer = window.setTimeout(function() {
      replayStartTimer = null;
      if (!analyticsAllowed || replayStarted) return;
      if (!bypassDelay && typeof window.requestIdleCallback === 'function') {
        replayIdleHandle = window.requestIdleCallback(function() {
          replayIdleHandle = null;
          startReplay();
        }, { timeout: 1500 });
        return;
      }
      startReplay();
    }, remainingDelay);
  }

  function requestHighIntentReplay() {
    forceReplayForSession();
    if (sdkReady) scheduleReplayStart(true);
  }

  function cancelScheduledSdkStart() {
    if (sdkStartTimer !== null) {
      window.clearTimeout(sdkStartTimer);
      sdkStartTimer = null;
    }
    if (sdkIdleHandle !== null && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(sdkIdleHandle);
      sdkIdleHandle = null;
    }
  }

  function scheduleSdkStart() {
    if (sdkStarted || sdkStartTimer !== null || sdkIdleHandle !== null || !analyticsAllowed) return;

    /* PostHog conserva los eventos en `pending`, por lo que no necesita competir
       con la pintura inicial. Se inicia después del tramo crítico y durante un
       hueco del hilo principal; en debug sigue siendo inmediato para facilitar
       las pruebas del equipo. */
    var configuredDelay = Number(config.analyticsDelayMs);
    if (!isFinite(configuredDelay)) configuredDelay = 3500;
    configuredDelay = Math.max(0, configuredDelay);
    var elapsed = window.performance && typeof window.performance.now === 'function'
      ? window.performance.now()
      : 0;
    var bypassDelay = debugRequested && !debugDelayRequested;
    var remainingDelay = bypassDelay ? 0 : Math.max(0, configuredDelay - elapsed);

    sdkStartTimer = window.setTimeout(function() {
      sdkStartTimer = null;
      if (!analyticsAllowed || sdkStarted) return;
      if (!bypassDelay && typeof window.requestIdleCallback === 'function') {
        sdkIdleHandle = window.requestIdleCallback(function() {
          sdkIdleHandle = null;
          startSdk();
        }, { timeout: 1500 });
        return;
      }
      startSdk();
    }, remainingDelay);
  }

  function setConsent(allowed) {
    consentResolved = true;
    analyticsAllowed = Boolean(allowed);
    if (analyticsAllowed) {
      /* La cookie solo se crea o lee después del permiso de analítica. */
      marketingAttribution = resolveMarketingAttribution();
      if (sdkReady && window.posthog && window.posthog.has_opted_out_capturing()) {
        window.posthog.opt_in_capturing();
      }
      if (sdkReady && window.posthog && typeof window.posthog.register === 'function') {
        window.posthog.register(eventContext());
      }
      scheduleSdkStart();
      if (sdkReady) scheduleReplayStart(isHighIntentReplayPage() || isReplayForcedForSession());
      flush();
    } else {
      marketingAttribution = {};
      deleteCookie(MARKETING_ATTRIBUTION_COOKIE);
      cancelScheduledSdkStart();
      cancelScheduledReplayStart();
      pending.length = 0;
      if (sdkReady && window.posthog) {
        window.posthog.stopSessionRecording();
        replayStarted = false;
        window.posthog.opt_out_capturing();
      }
    }
  }

  function privacyAllowsAnalytics() {
    return Boolean(
      window.Shopify &&
      Shopify.customerPrivacy &&
      typeof Shopify.customerPrivacy.analyticsProcessingAllowed === 'function' &&
      Shopify.customerPrivacy.analyticsProcessingAllowed()
    );
  }

  function loadPrivacy() {
    if (debugRequested && !isProduction) {
      setConsent(true);
      return;
    }

    document.addEventListener('visitorConsentCollected', function() {
      setConsent(privacyAllowsAnalytics());
    });

    if (!window.Shopify || typeof Shopify.loadFeatures !== 'function') return;
    Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function(error) {
      if (error) return;
      setConsent(privacyAllowsAnalytics());
    });
  }

  function productProperties(element) {
    var product = element && element.closest ? element.closest('[data-analytics-product]') : null;
    var fallback = config.product || {};
    var source = product || (element && element.dataset ? element : null);
    return compact({
      product_id: source && source.dataset.productId ? source.dataset.productId : fallback.id,
      product_handle: source && source.dataset.productHandle ? source.dataset.productHandle : fallback.handle,
      product_title: source && source.dataset.productTitle ? source.dataset.productTitle : fallback.title,
      product_vendor: source && source.dataset.productVendor ? source.dataset.productVendor : fallback.vendor,
      product_type: source && source.dataset.productType ? source.dataset.productType : fallback.productType,
      variant_id: element && element.dataset ? element.dataset.variantId : fallback.variantId
    });
  }

  function sectionProperties(element) {
    var section = element && element.closest ? element.closest('[data-analytics-section]') : null;
    if (!section) return {};
    return compact({
      section_type: section.dataset.analyticsSection,
      section_id: section.dataset.sectionId,
      section_title: section.dataset.sectionTitle,
      section_collection: section.dataset.collectionHandle
    });
  }

  function cardPosition(element) {
    var card = element && element.closest ? element.closest('[data-analytics-product]') : null;
    if (!card || !card.parentElement) return null;
    var positionNode = card;
    if (card.parentElement.matches('.slider-slide, [data-trg-item]')) {
      positionNode = card.parentElement;
    }
    var siblings = Array.prototype.filter.call(
      positionNode.parentElement.children,
      function(child) {
        return child.matches('.slider-slide, [data-trg-item]');
      }
    );
    if (!siblings.length) {
      siblings = Array.prototype.filter.call(positionNode.parentElement.children, function(child) {
        return child.matches('[data-analytics-product]');
      });
    }
    var index = siblings.indexOf(positionNode);
    return index >= 0 ? index + 1 : null;
  }

  function instrumentSections() {
    var sections = document.querySelectorAll('[data-analytics-section]');
    if (!sections.length || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.25) return;
        var section = entry.target;
        var handles = Array.prototype.map.call(
          section.querySelectorAll('[data-analytics-product]'),
          function(card) { return card.dataset.productHandle; }
        ).filter(function(handle, index, all) {
          return Boolean(handle) && all.indexOf(handle) === index;
        }).slice(0, 20);
        send('tms_section_viewed', Object.assign(sectionProperties(section), {
          product_handles: handles,
          product_count_rendered: handles.length
        }));
        observer.unobserve(section);
      });
    }, { threshold: [0.25] });

    sections.forEach(function(section) { observer.observe(section); });
  }

  function instrumentClicks() {
    var lastWhatsAppElement = null;
    var lastWhatsAppAt = 0;

    function captureWhatsApp(link) {
      var now = Date.now();
      if (lastWhatsAppElement === link && now - lastWhatsAppAt < 1500) return;
      lastWhatsAppElement = link;
      lastWhatsAppAt = now;
      send('tms_whatsapp_clicked', Object.assign(
        productProperties(link),
        sectionProperties(link),
        {
          source: link.classList.contains('wa-float')
            ? 'floating_button'
            : link.classList.contains('whatsapp-button')
              ? 'header'
              : link.classList.contains('product-form__advisor')
                ? 'product_advisor'
                : 'other'
        }
      ));
    }

    /* pointerdown ocurre antes de que el enlace externo abra otra pestaña.
       click queda como respaldo para activación por teclado; el guard evita
       que una interacción normal produzca dos eventos. */
    document.addEventListener('pointerdown', function(event) {
      var whatsapp = event.target.closest('a[href*="wa.me"], .wa-float');
      if (whatsapp) captureWhatsApp(whatsapp);
    }, { passive: true });

    document.addEventListener('click', function(event) {
      var target = event.target;
      var addButton = target.closest('[data-quick-add], [data-pcard-atc], [data-add-to-cart]');
      if (addButton && !addButton.disabled) {
        requestHighIntentReplay();
        send('tms_add_to_cart_clicked', Object.assign(
          productProperties(addButton),
          sectionProperties(addButton),
          { source: addButton.closest('form[data-product-form]') ? 'product_page' : 'product_card' }
        ));
        return;
      }

      var checkout = target.closest('[data-cart-checkout], [data-mc-checkout]');
      if (checkout && !checkout.disabled) {
        requestHighIntentReplay();
        send('tms_checkout_clicked', {
          source: checkout.hasAttribute('data-mc-checkout') ? 'mini_cart' : 'cart_page'
        });
        return;
      }

      var wishlist = target.closest('[data-wishlist-toggle]');
      if (wishlist) {
        send('tms_wishlist_toggled', Object.assign(
          productProperties(wishlist),
          sectionProperties(wishlist),
          { action: wishlist.getAttribute('aria-pressed') === 'true' ? 'removed' : 'added' }
        ));
        return;
      }

      var whatsapp = target.closest('a[href*="wa.me"], .wa-float');
      if (whatsapp) {
        captureWhatsApp(whatsapp);
        return;
      }

      var spotlightThumb = target.closest('[data-thumb][data-analytics-product]');
      if (spotlightThumb) {
        send('tms_spotlight_product_selected', Object.assign(
          productProperties(spotlightThumb),
          sectionProperties(spotlightThumb),
          { position: parseInt(spotlightThumb.dataset.thumb, 10) + 1 }
        ));
        return;
      }

      var productLink = target.closest('[data-analytics-product] a[href*="/products/"], a[data-analytics-product-link]');
      if (productLink) {
        send('tms_product_clicked', Object.assign(
          productProperties(productLink),
          sectionProperties(productLink),
          { position: cardPosition(productLink), destination: productLink.pathname }
        ));
        return;
      }

      var sectionLink = target.closest('[data-analytics-section] a[href]');
      if (sectionLink) {
        send('tms_section_link_clicked', Object.assign(
          sectionProperties(sectionLink),
          { label: sectionLink.textContent.trim().slice(0, 100), destination: sectionLink.pathname }
        ));
      }
    });
  }

  function instrumentPage() {
    instrumentSections();
    instrumentClicks();

    if (isHighIntentReplayPage()) requestHighIntentReplay();

    if (config.pageType === '404') {
      send('tms_404_viewed', {
        path: window.location.pathname,
        referrer: document.referrer || null
      });
    }

    if (config.pageType === 'product' && config.product) {
      send('tms_product_page_viewed', productProperties(document.body));
    }
  }

  loadPrivacy();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', instrumentPage);
  } else {
    instrumentPage();
  }
})();
